import { useState, useCallback, useMemo } from 'react'

export const TASK_QUALIFIER_FIELDS = [
  { key: 'qualifier_2', label: 'Atributo A' },
  { key: 'qualifier_3', label: 'Atributo B' },
  { key: 'qualifier_4', label: 'Atributo C' },
  { key: 'qualifier_5', label: 'Atributo D' },
  { key: 'qualifier_6', label: 'Atributo E' }
]

export const TASK_EMPTY_QUALIFIERS = Object.fromEntries(
  TASK_QUALIFIER_FIELDS.map(({ key }) => [key, []])
)

export const TASK_EMPTY_SELECTED_QUALIFIERS = Object.fromEntries(
  TASK_QUALIFIER_FIELDS.map(({ key }) => [key, []])
)

export const buildQualifierOptions = (qualifiers, key) =>
  (qualifiers?.[key] || []).map((value) => ({
    value,
    label: value,
    [key]: value
  }))

export const buildAllQualifierOptions = (qualifiers) =>
  Object.fromEntries(
    TASK_QUALIFIER_FIELDS.map(({ key }) => [key, buildQualifierOptions(qualifiers, key)])
  )

export const hasAnyTaskQualifierSelected = (selected) =>
  TASK_QUALIFIER_FIELDS.some(({ key }) => (selected?.[key] || []).length > 0)

export const validateAtLeastOneTaskQualifier = (...args) => {
  const selected =
    args.length === 1 && typeof args[0] === 'object' && !Array.isArray(args[0])
      ? args[0]
      : TASK_QUALIFIER_FIELDS.reduce(
          (acc, { key }, index) => {
            acc[key] = args[index] || []
            return acc
          },
          { ...TASK_EMPTY_SELECTED_QUALIFIERS }
        )

  return hasAnyTaskQualifierSelected(selected) ? 'success' : null
}

export const getCollectDataQualifierFields = (selected) =>
  Object.fromEntries(TASK_QUALIFIER_FIELDS.map(({ key }) => [key, selected?.[key] || []]))

export const applyClonedTaskQualifiers = (taskData, setSelectedQualifiers) => {
  const cloned = { ...TASK_EMPTY_SELECTED_QUALIFIERS }

  TASK_QUALIFIER_FIELDS.forEach(({ key }) => {
    if (taskData?.[key]?.length > 0) {
      cloned[key] = taskData[key]
    }
  })

  setSelectedQualifiers(cloned)
}

export function useTaskAttributeQualifiers() {
  const [qualifiers, setQualifiers] = useState(TASK_EMPTY_QUALIFIERS)
  const [selectedQualifiers, setSelectedQualifiers] = useState(TASK_EMPTY_SELECTED_QUALIFIERS)

  const handleChangeQualifier = useCallback((key, event) => {
    setSelectedQualifiers((prev) => ({
      ...prev,
      [key]: event != null ? event.map((e) => e.value) : []
    }))
  }, [])

  const resetSelectedQualifiers = useCallback(() => {
    setSelectedQualifiers(TASK_EMPTY_SELECTED_QUALIFIERS)
  }, [])

  const qualifierOptions = useMemo(() => buildAllQualifierOptions(qualifiers), [qualifiers])

  const isSomeQualifierSelected = useCallback(
    () => hasAnyTaskQualifierSelected(selectedQualifiers),
    [selectedQualifiers]
  )

  const validateAtLeastOneQualifier = useCallback(
    (...args) => validateAtLeastOneTaskQualifier(...args),
    []
  )

  return {
    qualifiers,
    setQualifiers,
    selectedQualifiers,
    setSelectedQualifiers,
    qualifierOptions,
    handleChangeQualifier,
    resetSelectedQualifiers,
    isSomeQualifierSelected,
    validateAtLeastOneQualifier,
    getCollectDataQualifierFields: () => getCollectDataQualifierFields(selectedQualifiers),
    applyClonedTaskQualifiers: (taskData) =>
      applyClonedTaskQualifiers(taskData, setSelectedQualifiers)
  }
}
